import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { apiClient } from '../../api/client';
import { useCanWriteCompanySettings } from '../../hooks/useCanWriteCompanySettings';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { toast } from 'sonner';

const inputCls = 'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm placeholder-fieldiq-muted focus:outline-none focus:border-fieldiq-accent/50 disabled:opacity-60 disabled:cursor-not-allowed';

/** Matches the API zod bound and the companies_ticket_number_padding_range CHECK. */
const MIN_PADDING = 1;
const MAX_PADDING = 12;

/**
 * Envelope-safe unwrap. `res.data?.data ?? res.data` reads the ENVELOPE as data
 * when the API answers `{success: true, data: null}` — see CLAUDE.md.
 */
function unwrap(body: any): any {
  if (body && typeof body === 'object' && 'data' in body) return body.data;
  return body;
}

interface ToggleRowProps {
  label: string;
  desc?: string;
  enabled: boolean;
  onChange: () => void;
  disabled?: boolean;
}

function ToggleRow({ label, desc, enabled, onChange, disabled }: ToggleRowProps) {
  return (
    <div className="flex items-center justify-between bg-fieldiq-card border border-fieldiq-border rounded-xl p-4">
      <div className="flex-1 mr-4">
        <p className="text-sm font-medium text-white">{label}</p>
        {desc && <p className="text-xs text-fieldiq-muted mt-0.5">{desc}</p>}
      </div>
      <button onClick={onChange} disabled={disabled} role="switch" aria-checked={enabled} aria-label={label}
        className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors shrink-0 disabled:cursor-not-allowed disabled:opacity-60 ${enabled ? 'bg-fieldiq-accent' : 'bg-fieldiq-border'}`}>
        <span className={`inline-block h-5 w-5 rounded-full bg-white transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
      </button>
    </div>
  );
}

/*
 * Only the settings something reads. The endpoint also stores
 * show_company_logo, show_tax_breakdown, show_crew_certifications,
 * require_photos, require_notes and auto_calculate_pay; they save, and nothing
 * — no PDF, ticket screen, payroll run or API path — reads any of them back.
 * They are left out of this page rather than shown as switches that do
 * nothing, and because the PATCH carries only the keys an edit touched, a save
 * here never writes them. Wire a reader up before bringing one back.
 *
 * show_pay_to_employees IS live — GET /employee/my-pay refuses a field worker
 * their own pay while it is off — and it is a payroll-visibility setting, not
 * a ticket one. It stays here for now; moving it is a separate call.
 *
 * require_price_book_link IS live (api#1042, 0508): POST /tickets/:id/approve
 * refuses a ticket with an unlinked priced line while it is on, unless the
 * company has no price book at all. Off (the column default) approves and
 * warns. It is a billing rule, but it is stored on this endpoint, so it sits
 * here under this page's Save rather than on Invoice Settings, which writes
 * /settings/invoice and would need a second save path for one key.
 */
export default function FieldTicketsPage() {
  const queryClient = useQueryClient();
  // PATCH /settings/field-tickets is requireRole('admin'), non-strict.
  const canWrite = useCanWriteCompanySettings();

  const query = useQuery({
    queryKey: ['settings', 'field-tickets'],
    queryFn: () => apiClient.get('/settings/field-tickets').then(r => unwrap(r.data) ?? {}),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const { data, isLoading } = query;
  // A failed read must not render the form's defaults: "off" on the approval
  // gate would be a plausible, possibly wrong, statement about the company.
  const failure = queryLoadFailure(query);

  const [form, setForm] = useState({
    ticket_number_prefix: 'FT-',
    next_ticket_number: 1,
    ticket_number_padding: 5,
    show_pay_to_employees: false,
    require_price_book_link: false,
  });
  /**
   * Exactly which keys this edit touched. The PATCH carries these and only these.
   *
   * Two reasons, both load-bearing:
   *  - PATCH /settings/field-tickets validates with a `.strict()` zod schema, so
   *    an unrecognised key is a 400, not an ignored field.
   *  - `next_ticket_number` is a live counter the ticket-create path advances.
   *    Echoing back the value this page read at mount would walk it BACKWARDS if
   *    a ticket was created in between — so a toggle-only save must not send it.
   */
  const [dirtyKeys, setDirtyKeys] = useState<Set<string>>(new Set());
  const [paddingError, setPaddingError] = useState('');
  const [numberError, setNumberError] = useState('');

  useEffect(() => {
    if (data) {
      setForm(prev => ({
        ...prev,
        ticket_number_prefix: data.ticket_number_prefix ?? prev.ticket_number_prefix,
        next_ticket_number: data.next_ticket_number ?? prev.next_ticket_number,
        ticket_number_padding: data.ticket_number_padding ?? prev.ticket_number_padding,
        show_pay_to_employees: data.show_pay_to_employees ?? prev.show_pay_to_employees,
        require_price_book_link: data.require_price_book_link ?? prev.require_price_book_link,
      }));
      setDirtyKeys(new Set());
      setPaddingError('');
      setNumberError('');
    }
  }, [data]);

  const update = (key: string, value: any) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setDirtyKeys(prev => new Set(prev).add(key));
    if (key === 'ticket_number_padding') setPaddingError('');
    if (key === 'next_ticket_number') setNumberError('');
  };

  const toggle = (key: string) => update(key, !(form as any)[key]);

  const dirty = dirtyKeys.size > 0;

  const saveMutation = useMutation({
    mutationFn: () => {
      // Exactly the changed keys, all of them names the .strict() schema accepts.
      const payload: Record<string, any> = {};
      dirtyKeys.forEach(k => { payload[k] = (form as any)[k]; });
      return apiClient.patch('/settings/field-tickets', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'field-tickets'] });
      setDirtyKeys(new Set());
      toast.success('Field ticket settings saved');
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to save'),
  });

  const onSave = () => {
    const p = form.ticket_number_padding;
    if (!Number.isInteger(p) || p < MIN_PADDING || p > MAX_PADDING) {
      setPaddingError(`Padding width must be between ${MIN_PADDING} and ${MAX_PADDING}`);
      return;
    }
    const n = form.next_ticket_number;
    if (!Number.isInteger(n) || n < 1) {
      setNumberError('Next ticket number must be 1 or greater');
      return;
    }
    saveMutation.mutate();
  };

  if (failure) {
    return <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="field ticket settings" />;
  }

  if (isLoading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-fieldiq-accent animate-spin" /></div>;
  }

  const padded = Number.isFinite(form.next_ticket_number) && Number.isInteger(form.ticket_number_padding)
    && form.ticket_number_padding >= MIN_PADDING && form.ticket_number_padding <= MAX_PADDING
    ? `${form.ticket_number_prefix}${String(form.next_ticket_number).padStart(form.ticket_number_padding, '0')}`
    : '—';

  return (
    <div className="min-h-full">
      {/* The Operations workspace renders the title and the tab strip. */}
      <div className="px-6 py-4 space-y-6">
        {canWrite ? (
          <div className="flex justify-end">
            <button
              onClick={onSave}
              disabled={!dirty || saveMutation.isPending}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                dirty ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'bg-fieldiq-border text-fieldiq-muted cursor-not-allowed'
              }`}
            >
              {saveMutation.isPending ? <Loader2 size={16} className="animate-spin" /> : null}
              {saveMutation.isPending ? 'Saving...' : 'Save'}
            </button>
          </div>
        ) : (
          <p className="text-xs text-fieldiq-muted">Only an admin can change field ticket settings.</p>
        )}

        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-white">Numbering</h2>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-sm font-medium text-white block mb-1">Ticket Prefix</label>
              <input
                className={inputCls}
                value={form.ticket_number_prefix}
                disabled={!canWrite}
                aria-label="Ticket Prefix"
                onChange={e => update('ticket_number_prefix', e.target.value)}
                placeholder="FT-"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-white block mb-1">Next Ticket Number</label>
              <input
                type="number"
                min="1"
                className={inputCls}
                value={form.next_ticket_number}
                disabled={!canWrite}
                aria-label="Next Ticket Number"
                onChange={e => update('next_ticket_number', parseInt(e.target.value) || 1)}
              />
              {numberError && <p className="text-xs text-red-400 mt-1">{numberError}</p>}
            </div>

            <div>
              <label className="text-sm font-medium text-white block mb-1">Padding Width</label>
              <input
                type="number"
                min={MIN_PADDING}
                max={MAX_PADDING}
                className={inputCls}
                value={form.ticket_number_padding}
                disabled={!canWrite}
                aria-label="Padding Width"
                onChange={e => update('ticket_number_padding', parseInt(e.target.value))}
              />
              {paddingError && <p className="text-xs text-red-400 mt-1">{paddingError}</p>}
            </div>
          </div>

          <p className="text-xs text-fieldiq-muted">
            Preview: <span className="text-fieldiq-accent font-mono">{padded}</span>
          </p>
        </section>

        <section className="space-y-3">
          <ToggleRow label="Show pay to field workers" desc="Lets a field worker see their own pay in the app." enabled={form.show_pay_to_employees} onChange={() => toggle('show_pay_to_employees')} disabled={!canWrite} />
        </section>

        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-white">Billing</h2>
          <ToggleRow
            label="Require price-book link before approval"
            desc="When on, tickets can't be approved until every line is linked to the price book or marked off-book."
            enabled={form.require_price_book_link}
            onChange={() => toggle('require_price_book_link')}
            disabled={!canWrite}
          />
        </section>
      </div>
    </div>
  );
}
